/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {List, ListItemText} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import useDeviceManufacturingInfo from '@ion/query/src/hooks/useDeviceManufacturingInfo';
import {useDeviceViewRefresh} from '../../hooks/useDeviceViewRefresh';
import SplitInformationView from './SplitInformationView';
import TabContainer from './TabContainer';
import useVbusAccess from './useVbusAccess';
import {getMFGData, getMFGInfo} from './ViewDeviceEditorUtils';

export interface ManufacturingTabType {
  deviceIdentifier: string;
  isMDTDevice: boolean;
  tabIndex: Number;
  isCMF?: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  paraStyle : {
    margin : '10px 4px 20px 6px'
  },
  headerItem : {
    ...dlsAlt('typography', 'label1'),
    fontWeight : '700 !important',
    color      : dlsColor('grey1')
  },
  bodyItem : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('grey1'),
    fontSize      : '14px',
    letterSpacing : 0,
    padding       : '5px'
  },
  listSecText : {
    color    : dlsColor('black'),
    fontSize : '14px'
  }
}));

/**
 * ManufacturingTab component that displays the Device manufacturing
 * data for the selected Device Details tab.
 */
export const ManufacturingTab = ({
  deviceIdentifier,
  isMDTDevice,
  isCMF,
  tabIndex = 0
}: ManufacturingTabType) => {
  const classes     = useStyles();
  const isCalampSup = useVbusAccess(true);

  const {
    manufacturingInfo,
    refresh: onRefreshManufacturingInfo
  } = useDeviceManufacturingInfo(deviceIdentifier, isMDTDevice, !!deviceIdentifier);

  const refreshTab = useCallback(onRefreshManufacturingInfo, []);

  useDeviceViewRefresh(refreshTab);

  const mfgInfo     = getMFGInfo(manufacturingInfo);
  const {
    model,
    rawData,
    androidOS,
    mfgdataraw,
    partNumber,
    buildNumber,
    description,
    kernelVesion,
    unitpartNumber,
    currentMobileId,
    boardlevelPartNumber
  } = manufacturingInfo || {};

  let highlightedItems;
  if (isCMF) {
    highlightedItems = <List>
      <ListItemText className={classes.headerItem}
        primary={translate('admin-devices:view.manufacturing.androidOSVersion')}
        secondary={<p className={classes.paraStyle}><span className={classes.listSecText} >{androidOS ?? ''}</span></p>} />
      <ListItemText className={classes.headerItem}
        primary={translate('admin-devices:view.manufacturing.buildNumber')}
        secondary={<p className={classes.paraStyle}><span className={classes.listSecText}>{buildNumber ?? ''}</span></p>} />
      <ListItemText className={classes.headerItem}
        primary={translate('admin-devices:view.manufacturing.mobileId')}
        secondary={<p className={classes.paraStyle}><span className={classes.listSecText}>{currentMobileId ?? ''}</span></p>} />
      <ListItemText className={classes.headerItem}
        primary={translate('admin-devices:view.manufacturing.kernelVersion')}
        secondary={<p className={classes.paraStyle}><span className={classes.listSecText}>{kernelVesion ?? ''}</span></p>} />
      <ListItemText className={classes.headerItem}
        primary={translate('admin-devices:view.manufacturing.model')}
        secondary={<p className={classes.paraStyle}><span className={classes.listSecText}>{model ?? ''}</span></p>} />
    </List>;
  } else {
    highlightedItems = <List>
      <ListItemText className={classes.headerItem}
        primary={translate('admin-devices:view.manufacturing.unitPartNumber')}
        secondary={<p className={classes.paraStyle}><span className={classes.listSecText} >
          {unitpartNumber ?? partNumber}
        </span></p>} />
      <ListItemText className={classes.headerItem}
        primary={translate('admin-devices:view.manufacturing.boardPartNumber')}
        secondary={<p className={classes.paraStyle}><span className={classes.listSecText}>
          {boardlevelPartNumber ?? partNumber}
        </span></p>} />
      <ListItemText className={classes.headerItem}
        primary={translate('admin-devices:view.manufacturing.description')}
        secondary={<p className={classes.paraStyle}><span className={classes.listSecText}>{description ?? ''}</span></p>} />
    </List>;
  }

  return (
    <TabContainer key={'detailsTab'} tabIndex={tabIndex}>
      <SplitInformationView isCalampSup={isCalampSup}
        text={isCalampSup ? manufacturingInfo : getMFGData(rawData, mfgInfo)}>
        {highlightedItems}
      </SplitInformationView>
    </TabContainer>
  );
};

export default ManufacturingTab;
